<template>
  <v-list-item
    :class="{ 'cursor-pointer': clickable || nav, 'll-nav-current-y': nav && current }"
    :aria-current="nav && current ? 'page' : undefined"
    :data-cy="dataCy"
    @click="handleClick"
  >
    <v-list-item-title :class="titleClass">
      <v-icon v-if="icon" size="16" class="mr-1">{{ icon }}</v-icon>
      <slot>{{ title }}</slot>
    </v-list-item-title>
  </v-list-item>
</template>

<script setup lang="ts">
import { computed } from 'vue';

/**
 * Shared wrapper for the "informational row" / "clickable action row" pattern that kept getting
 * hand-rolled with raw `v-list-item`/`v-list-item-title` inside dropdown `#no-data`/`#append-item`
 * slots across the app (LienFormPanel, ClientMatterSearch, BillableTypePicker, AssignDistribution,
 * and others) — one shared component instead of each place re-inlining its own copy.
 * With `nav` it is also the row of a vertical navigation list (Settings, Overview).
 */
const COLOR_CLASSES: Record<string, string> = {
  error: 'text-red-600',
  success: 'text-green-700',
  muted: 'text-gray-500',
};

const props = defineProps<{
  /** Simple text content — use the default slot instead for anything richer. */
  title?: string;
  /** Leading mdi icon, e.g. `mdi-plus`. */
  icon?: string;
  color?: 'error' | 'success' | 'muted';
  /** Adds a pointer cursor and emphasized weight, and makes `@click` actually fire. */
  clickable?: boolean;
  /** A row in a vertical nav list: always clicks, and only the `current` row is emphasized. */
  nav?: boolean;
  /** With `nav`, the page the user is on: the shared bar and tint, and `aria-current="page"`. */
  current?: boolean;
  dataCy?: string;
}>();

const emit = defineEmits<{
  click: [];
}>();

const titleClass = computed(() => {
  const colorClass = props.color ? COLOR_CLASSES[props.color] : '';
  // Inside a `<v-list nav>`, Vuetify's title rule (13px, weight 500) outranks plain utilities.
  if (props.nav) return ['!text-sm', colorClass, props.current ? '!font-medium' : '!font-normal'];
  return ['text-sm', colorClass, props.clickable ? 'font-medium' : ''];
});

function handleClick(): void {
  if (props.clickable || props.nav) emit('click');
}
</script>
